import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { MotionConfig } from "motion/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ApiError, onSessionExpired } from "@/shared/api/client";
import { qk } from "@/shared/api/keys";
import type { Session } from "@/shared/api/types";
import { t } from "@/shared/lib/i18n";
import { ThemeProvider } from "@/shared/lib/theme";
import {
  observeRouterTransitions,
  startBrowserPerformanceObservers,
} from "@/shared/observability/performance";
import { clearOfflineAccount } from "@/shared/offline";
import { isNative, startNativeHealthTelemetry } from "@/shared/platform/native";
import { ConfirmProvider } from "@/shared/ui/primitives/Confirm";
import { notifyToast, ToastProvider } from "@/shared/ui/primitives/Toast";
import { makeRouter } from "./router";

// Register at application boot, before authentication resolves. This lets the
// sign-in and recovery surfaces receive the deployment shell and share-target
// handling too; update prompts remain a signed-in shell concern.
if (import.meta.env.PROD && !isNative() && "serviceWorker" in navigator) {
  void navigator.serviceWorker.register("/sw.js");
}

/**
 * Boot.
 *
 * The query client's defaults are chosen for a personal dataset that one person
 * changes: refetch when a tab regains focus (you may have edited on your
 * phone), keep data for a while (it rarely changes behind your back), and never
 * retry an authorisation failure (retrying a dead session only delays the
 * sign-in screen).
 *
 * This remains separate from `main.tsx` so startup stays explicit and the API
 * client is created only when the application boot sequence begins.
 */

export function start() {
  startBrowserPerformanceObservers();
  void startNativeHealthTelemetry();
  const queryClient = new QueryClient({
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // Form-level authentication errors belong beside the fields. Repeating
        // the same sentence in a global toast makes one failure look like two.
        if (mutation.meta?.suppressGlobalError === true) return;
        notifyToast({
          title: t("copy.c3e13b03dc99"),
          description: error instanceof ApiError ? error.userMessage : t("copy.a5ec4079a398"),
          tone: "error",
          duration: 6500,
        });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 10 * 60_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
      },
      mutations: { retry: false },
    },
  });

  const router = makeRouter(queryClient);
  observeRouterTransitions(router);

  // A session that ends server-side must end here too, or the application keeps
  // showing a cached life to somebody who is no longer signed in.
  onSessionExpired(() => {
    const accountId = queryClient.getQueryData<Session>(qk.session)?.user.id;
    if (accountId) void clearOfflineAccount(accountId);
    queryClient.clear();
    if (!window.location.pathname.startsWith("/giris")) {
      const next = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      router.navigate({ to: "/giris", search: { next } });
    }
  });

  const container = document.getElementById("root");
  if (!container) throw new Error("#root bulunamadı");

  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <MotionConfig reducedMotion="user">
            <ToastProvider>
              <ConfirmProvider>
                <RouterProvider router={router} />
              </ConfirmProvider>
            </ToastProvider>
          </MotionConfig>
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}
